/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { generateUUID } from "shared/lib/generateUUID"
import { mergeRefs } from "shared/lib/mergeRef"
import { stopPropagation } from "shared/lib/stopPropagation"
import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useEvent } from "shared/lib/useEvent"

import { useInputDescriptionIDs } from "../../accessibility/useInputAriaDescription"
import { CheckboxLabel } from "./CheckboxLabel"

export interface CheckboxProps
  extends Omit<
    React.PropsWithRef<React.HTMLProps<HTMLInputElement>>,
    | "type"
    | "onChange"
    | "value"
    | "defaultValue"
    | "checked"
    | "defaultChecked"
    | "label"
  > {
  value?: boolean
  defaultValue?: boolean
  loading?: boolean
  label?: React.ReactNode
  onChange?: (value: boolean) => void
  stopClickPropagation?: boolean
}

export const Checkbox = React.memo(
  React.forwardRef(
    (
      {
        id,
        loading,
        disabled,
        onChange,
        label,
        value,
        defaultValue,
        className,
        autoFocus,
        stopClickPropagation = true,
        ...rest
      }: CheckboxProps,
      ref: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { t } = useTranslation()
      const idRef = useRef(id || generateUUID())
      const { ariaDescribedBy } = useInputDescriptionIDs({
        additionalDescriptions: rest["aria-describedby"]
      })

      const handleChange: React.ChangeEventHandler<HTMLInputElement> = useEvent(
        () => {
          onChange?.(!value)
        }
      )
      const internalRef = useRef(null)

      useAutoFocus({ ref: internalRef, autoFocus })

      return (
        <div
          onClick={stopClickPropagation ? stopPropagation : undefined}
          className={classNames("form-check", "checkbox", className)}
        >
          <input
            type="checkbox"
            id={idRef.current}
            ref={mergeRefs(ref, internalRef)}
            {...rest}
            className={classNames("form-check-input")}
            checked={value ?? undefined}
            defaultChecked={defaultValue ?? undefined}
            onChange={handleChange}
            disabled={disabled || loading}
            aria-describedby={ariaDescribedBy}
            // While loading, CheckboxLabel renders a Skeleton instead of the
            // <label for>, so the input would otherwise have no accessible
            // name. Only kick in when the caller has not set one explicitly.
            aria-label={
              rest["aria-label"] || (loading ? t("General.Loading") : undefined)
            }
          />
          <CheckboxLabel id={idRef.current} label={label} isLoading={loading} />
        </div>
      )
    }
  )
)
